import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import isEmpty from "../../../../utils/isEmpty";
import { callLogout, fetchUserInfo } from "../../../../store/Auth/actions";
import {
  fetchPrivateCompaniesList,
  fetchGovCompaniesList,
  fetchCompanyInfo,
} from "../../../../store/Home/actions";
import { resetErrors } from "../../../../store/Error/epics";
import useLegacyEffect from "../../../../hooks/useLegacyEffect";

const DropdownToggle = ({ label, onClick }) => (
  <button
    className="btn text-dark fs-6 d-flex align-items-center bg-white rounded px-2 py-1 shadow-sm"
    onClick={onClick}
  >
    <span className={label?.length < 10 ? "ms-5" : ""}>{label}</span>
    <i
      className="bi px-1 bi-caret-down-fill mt-1 mx-1"
      style={{ fontSize: "12px" }}
    />
  </button>
);

const SubMenu = ({ items, isAr }) => (
  <div
    className="dropdown-menu show position-absolute py-3"
    style={{
      top: 0,
      left: isAr ? "auto" : "100%",
      right: isAr ? "100%" : "auto",
      minWidth: 150,
    }}
  >
    {items?.map(({ key, label, disabled, onClick }) => (
      <button
        key={key}
        className={`dropdown-item  ${isAr ? "text-end" : "text-start"}`}
        disabled={disabled}
        onClick={onClick}
      >
        {label}
      </button>
    ))}
  </div>
);

const MenuSection = ({ section, isAr, subOpen, setSubOpen }) => {
  const { label, items, hasSub, onClick } = section;

  if (hasSub) {
    return (
      <div
        className="dropdown-item d-flex justify-content-between fs-6"
        onMouseEnter={() => setSubOpen(true)}
        onMouseLeave={() => setSubOpen(false)}
      >
        {label}
        <i className={`bi bi-caret-${isAr ? "left" : "right"}-fill`} />
        {subOpen && <SubMenu items={items} isAr={isAr} />}
      </div>
    );
  }

  return (
    <button
      className={`dropdown-item py-2 px-3 ${
        isAr ? "text-end" : "text-start"
      } fs-6`}
      onClick={onClick}
    >
      {label}
    </button>
  );
};

const Menu = ({ isLanding }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [subOpen, setSubOpen] = React.useState(false);
  const wrapperRef = React.useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const local = useSelector((s) => s.homeReducer.local);
  const user = useSelector((s) => s.authReducer.user);
  const companyInfo = useSelector((s) => s.homeReducer.companyInfo);
  const myCompaniesList = useSelector((s) => s.homeReducer.myCompaniesList);

  const isAr = local.isAr;

  // Load data effects
  useLegacyEffect(() => {
    dispatch(fetchUserInfo());
    dispatch(resetErrors());
  }, []);

  useLegacyEffect(() => {
    if (isEmpty(myCompaniesList)) {
      if (user.user_type === "PRIVATE") {
        dispatch(fetchPrivateCompaniesList({ is_login: false }));
      } else if (user.user_type === "GOVERNMENT") {
        dispatch(fetchGovCompaniesList(navigate, local));
      }
    }
  }, [user]);

  // Handlers
  const handleSelect = (companyId) => {
    const company = myCompaniesList?.find((c) => c.id === companyId);
    localStorage.setItem("companyName", company.name);
    dispatch(fetchCompanyInfo(companyId, navigate, true));
    setIsOpen(false);
  };
  const handleLogout = () => dispatch(callLogout());

  // Close on outside click
  React.useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSubOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Build menu data
  const topLabel = isLanding ? local.companiesList : local.changeCompany;
  const companyItems = (myCompaniesList || [])?.map(
    ({ id, name, name_en }) => ({
      key: id,
      label: isAr ? name : name_en,
      disabled: companyInfo?.name === name,
      onClick: () => handleSelect(id),
    })
  );

  const menuSections = [
    { label: topLabel, items: companyItems, hasSub: true },
    { label: local.signOut, onClick: handleLogout },
  ];

  const selectedDropDownName = !isEmpty(companyInfo)
    ? isAr
      ? companyInfo.name
      : companyInfo.name_en
    : localStorage.getItem("companyName");

  return (
    <div className="position-relative mx-4" ref={wrapperRef}>
      <DropdownToggle
        label={selectedDropDownName}
        onClick={() => setIsOpen((open) => !open)}
      />
      {isOpen ? (
        <div
          className="dropdown-menu show header-menu py-3 mt-1"
          style={{ minWidth: 250 }}
        >
          {menuSections?.map((sec, idx) => (
            <MenuSection
              key={idx}
              section={sec}
              isAr={isAr}
              subOpen={subOpen}
              setSubOpen={setSubOpen}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default Menu;
